import { useCallback, useRef, useState } from 'react'
import { getExcel } from '@/utils/request'
import dayjs from 'dayjs'
import { AxiosRequestConfig } from 'axios'
import { isPboScreenActive, notice } from '@/components/pbo/notice'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import MLoader from '@/utils/modal/MLoader'
import { HTTP_BAD_REQUEST } from '@/utils/consts'

interface Params {
  fileNamePrefix?: string
  config?: AxiosRequestConfig
  /** 실패 알림을 옛 알럿 모달 대신 새 디자인 알럿(notice.failure)으로 — pbo-core(2.0) 화면용(옵션 이름은 옛 이름 그대로, 2.0 안내 기준 "실패=알럿") */
  errorToast?: boolean
}

const DOWNLOAD_FAILED_MESSAGE = '다운로드에 실패했습니다.'

const readBlobText = (blob: Blob): Promise<string> => (typeof blob.text === 'function' ? blob.text() : new Response(blob).text())

const readServerMessage = async (e: unknown): Promise<string | undefined> => {
  const response = (e as { response?: { status?: number; data?: unknown } })?.response
  if (response?.status !== HTTP_BAD_REQUEST || !response.data) return undefined

  try {
    const raw = response.data instanceof Blob ? await readBlobText(response.data) : response.data
    const body = typeof raw === 'string' ? JSON.parse(raw) : raw
    const message = (body as { meta?: { message?: unknown } })?.meta?.message
    return typeof message === 'string' && message ? message : undefined
  } catch {
    return undefined
  }
}

function useExcelDownload<T = UnknownMap>({ fileNamePrefix = 'Excel', config = { method: 'get' }, errorToast = false }: Params = {}) {
  const [isLoading, setIsLoading] = useState(false)
  // config 는 최신 값을 ref 로 읽는다 — deps 에 넣으면 호출처가 인라인 객체를 넘길 때마다 onDownloadExcel 이 새로 만들어지고,
  //  빼면 그런 호출처에서 이전 config 가 쓰인다(#899 리뷰).
  const configRef = useRef(config)
  configRef.current = config

  const getFileNameFromDisposition = useCallback((disposition: string) => {
    const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-\\.]+)(?:; ?|$)/i
    const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i

    let fileName = ''
    if (utf8FilenameRegex.test(disposition)) {
      fileName = decodeURIComponent(utf8FilenameRegex.exec(disposition)?.[1] || '')
    } else {
      const filenameStart = disposition.toLowerCase().indexOf('filename=')

      if (filenameStart >= 0) {
        const partialDisposition = disposition.slice(filenameStart)
        const matches = asciiFilenameRegex.exec(partialDisposition)
        if (matches != null && matches[2]) fileName = matches[2]
      }
    }

    return fileName
  }, [])

  const onDownloadExcel = useCallback(
    async (url: string, params?: T) => {
      // 2.0 화면 여부는 요청을 시작한 시점에 정한다 — 응답 전에 다른 탭(1.0 iframe)으로 옮기면 실패 시점엔 2.0 화면이 가려져 옛 알럿이 뜬다(#899 리뷰).
      const fromPboScreen = errorToast || isPboScreenActive()
      try {
        if (!url) return

        setIsLoading(true)
        MLoader.show()

        const resp =
          configRef.current?.method === 'post'
            ? await getExcel(url, { ...configRef.current, data: params })
            : await getExcel(`${url}${Utils.qsStringify(params ?? {})}`, configRef.current)
        const blobUrl = URL.createObjectURL(resp.data)

        setIsLoading(false)
        MLoader.hide()

        const filename =
          getFileNameFromDisposition(resp.headers?.['content-disposition'] || '') || `${fileNamePrefix}_${dayjs().format('YYYYMMDDhms')}.xlsx`

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.click()
        URL.revokeObjectURL(blobUrl)
      } catch (e) {
        setIsLoading(false)
        MLoader.hide()

        const message = (await readServerMessage(e)) || DOWNLOAD_FAILED_MESSAGE
        if (fromPboScreen) notice.failure(message) // 2.0 화면: 실패=새 디자인 알럿(notice.ts). 옵션 이름은 옛 이름 그대로
        else MAlert.show(message)
      }
    },
    [fileNamePrefix, errorToast],
  )

  return {
    isLoading,
    onDownloadExcel,
  }
}

export default useExcelDownload
